"use client";

import { CopyField } from "@commerce/components/CopyField";
import {
	Page,
	PageHeader,
	SaveBar,
	SettingsRow,
	SettingsSection,
} from "@commerce/components/page";
import { orpc } from "@shared/lib/orpc";
import {
	useMutation,
	useQuery,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import { Badge } from "@ui/components/badge";
import { Button } from "@ui/components/button";
import { Checkbox } from "@ui/components/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@ui/components/dialog";
import {
	Field,
	FieldContent,
	FieldDescription,
	FieldLabel,
	FieldTitle,
} from "@ui/components/field";
import { DisabledReason } from "@ui/components/info-tip";
import { Input } from "@ui/components/input";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
} from "@ui/components/input-group";
import { AnchorLinkButton } from "@ui/components/link-button";
import { RadioGroup, RadioGroupItem } from "@ui/components/radio-group";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@ui/components/select";
import { Skeleton } from "@ui/components/skeleton";
import { Spinner } from "@ui/components/spinner";
import { Switch } from "@ui/components/switch";
import { Textarea } from "@ui/components/textarea";
import { toast } from "@ui/components/toast";
import { CheckIcon, CircleXIcon, ExternalLinkIcon } from "lucide-react";
import { useId, useState } from "react";
import { actionFailed } from "./OrderActions";
import type { OrderBackend, OrderingSettings, OrderStatus } from "./types";

const route = getRouteApi("/_saas/app/_org/$organizationSlug/orders/setup");

type BackendKind = OrderBackend["kind"];

const BACKENDS: Array<{ kind: BackendKind; title: string; detail: string }> = [
	{
		kind: "TICKET",
		title: "Order tickets in Hephon",
		detail: "Your team works orders on the Orders screen.",
	},
	{
		kind: "ORDERS_API",
		title: "Your orders API",
		detail: "Hephon sends each order to an endpoint you run.",
	},
	{
		kind: "SOURCE",
		title: "Your platform",
		detail: "Orders go to the platform your catalog comes from.",
	},
];

const PAYMENT_METHODS = [
	{ value: "cash", label: "Cash on delivery" },
	{ value: "card_on_delivery", label: "Card on delivery" },
	{ value: "link", label: "Payment link" },
] as const;

/** Status → the template purpose that tells the shopper about it when the window is closed. */
const SHOPPER_UPDATES: Array<{
	status: Exclude<OrderStatus, "PLACED">;
	label: string;
	purpose: string | null;
}> = [
	{ status: "CONFIRMED", label: "Accepted", purpose: "order_confirmed" },
	{ status: "IN_PROGRESS", label: "Picking started", purpose: null },
	{
		status: "READY_FOR_PICKUP",
		label: "Ready for pickup",
		purpose: "ready_for_pickup",
	},
	{
		status: "OUT_FOR_DELIVERY",
		label: "Out for delivery",
		purpose: "out_for_delivery",
	},
	{ status: "DELIVERED", label: "Delivered", purpose: "delivered" },
	{ status: "CANCELLED", label: "Cancelled", purpose: "cancelled" },
	{ status: "RETURNED", label: "Returned", purpose: null },
];

/**
 * Ordering setup (spec 5.7): how orders reach the retailer, delivery and payment, rules,
 * notifications and the messages shoppers get. The rules save together with one SaveBar;
 * the order system is its own section with its own save, because changing it is a decision.
 */
export function OrderingSetup({ organizationId }: { organizationId: string }) {
	const { organizationSlug } = route.useParams();
	const queryClient = useQueryClient();
	const settingsQuery = orpc.orders.settings.get.queryOptions({
		input: { organizationId },
	});
	const { data: saved } = useSuspenseQuery(settingsQuery);
	const [draft, setDraft] = useState<OrderingSettings>(saved);
	const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
	const save = useMutation(
		orpc.orders.settings.save.mutationOptions({
			onSuccess: (next) => {
				queryClient.setQueryData(settingsQuery.queryKey, next);
				setDraft(next);
				toast.add({ type: "success", title: "Ordering rules saved" });
			},
			onError: (error) => actionFailed(error, "Couldn't save the rules"),
		}),
	);
	const set = <K extends keyof OrderingSettings>(
		key: K,
		value: OrderingSettings[K],
	) => setDraft((prev) => ({ ...prev, [key]: value }));
	const setDelivery = <K extends keyof OrderingSettings["deliveryDefaults"]>(
		key: K,
		value: OrderingSettings["deliveryDefaults"][K],
	) =>
		setDraft((prev) => ({
			...prev,
			deliveryDefaults: { ...prev.deliveryDefaults, [key]: value },
		}));
	const problem =
		draft.paymentMethods.length === 0
			? "Choose at least one way to pay"
			: null;

	return (
		<Page width="narrow">
			<PageHeader
				title="Ordering setup"
				back={{
					label: "Orders",
					to: "/app/$organizationSlug/orders",
					params: { organizationSlug },
				}}
			/>

			<BackendSection organizationId={organizationId} />

			<SettingsSection
				title="Delivery and payment"
				info="Branches can override the fee, minimum and slots on the Branches screen."
			>
				<SettingsRow label="Ways to pay">
					<div className="flex flex-col gap-2">
						{PAYMENT_METHODS.map((method) => {
							const checked = draft.paymentMethods.includes(
								method.value,
							);
							const id = `pay-${method.value}`;
							return (
								<label
									key={method.value}
									htmlFor={id}
									className="flex items-center gap-2 text-sm"
								>
									<Checkbox
										id={id}
										checked={checked}
										onCheckedChange={(on) =>
											set(
												"paymentMethods",
												on
													? [
															...draft.paymentMethods,
															method.value,
														]
													: draft.paymentMethods.filter(
															(m) =>
																m !==
																method.value,
														),
											)
										}
									/>
									{method.label}
								</label>
							);
						})}
					</div>
				</SettingsRow>
				<SettingsRow label="Delivery fee" htmlFor="fee">
					<MoneyInput
						id="fee"
						value={draft.deliveryDefaults.fee}
						onChange={(v) => setDelivery("fee", v ?? 0)}
					/>
				</SettingsRow>
				<SettingsRow label="Free delivery above" htmlFor="free-above">
					<MoneyInput
						id="free-above"
						value={draft.deliveryDefaults.freeAbove}
						onChange={(v) => setDelivery("freeAbove", v)}
						placeholder="Never free"
					/>
				</SettingsRow>
				<SettingsRow label="Minimum order" htmlFor="minimum">
					<MoneyInput
						id="minimum"
						value={draft.deliveryDefaults.minimumOrder}
						onChange={(v) => setDelivery("minimumOrder", v)}
						placeholder="No minimum"
					/>
				</SettingsRow>
				<SettingsRow
					label="Delivery slots"
					info="How long each slot is, how soon the first one starts, and how many days ahead shoppers can choose."
				>
					<div className="grid w-full grid-cols-3 gap-2">
						<NumberField
							label="Slot, min"
							value={draft.deliveryDefaults.slotMinutes}
							min={15}
							onChange={(v) =>
								setDelivery("slotMinutes", v ?? 60)
							}
						/>
						<NumberField
							label="First in, min"
							value={draft.deliveryDefaults.leadMinutes}
							min={0}
							onChange={(v) => setDelivery("leadMinutes", v ?? 0)}
						/>
						<NumberField
							label="Days ahead"
							value={draft.deliveryDefaults.horizonDays}
							min={1}
							max={14}
							onChange={(v) => setDelivery("horizonDays", v ?? 2)}
						/>
					</div>
				</SettingsRow>
				<SettingsRow
					label="Orders per slot"
					htmlFor="capacity"
					info="A slot stops being offered once it has this many open orders."
				>
					<Input
						id="capacity"
						type="number"
						inputMode="numeric"
						min={1}
						value={draft.deliveryDefaults.capacityPerSlot ?? ""}
						placeholder="No limit"
						onChange={(e) =>
							setDelivery(
								"capacityPerSlot",
								e.target.value
									? Math.max(1, Number(e.target.value))
									: undefined,
							)
						}
					/>
				</SettingsRow>
				<SettingsRow label="Pickup in store" htmlFor="pickup">
					<Switch
						id="pickup"
						checked={draft.pickup}
						onCheckedChange={(on) => set("pickup", on)}
					/>
				</SettingsRow>
				<SettingsRow
					label="Checkout on WhatsApp"
					info="The checkout form is WhatsApp's own form. Buttons work everywhere, including where forms are blocked."
				>
					<Select
						items={{
							auto: "Form when it is healthy, else buttons",
							flow: "Always the form",
							buttons: "Always buttons",
						}}
						value={draft.checkoutMode}
						onValueChange={(v) =>
							v &&
							set(
								"checkoutMode",
								v as OrderingSettings["checkoutMode"],
							)
						}
					>
						<SelectTrigger aria-label="Checkout on WhatsApp">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="auto">
								Form when it is healthy, else buttons
							</SelectItem>
							<SelectItem value="flow">
								Always the form
							</SelectItem>
							<SelectItem value="buttons">
								Always buttons
							</SelectItem>
						</SelectContent>
					</Select>
				</SettingsRow>
			</SettingsSection>

			<SettingsSection title="Rules">
				<SettingsRow label="Shoppers can cancel until">
					<Select
						items={{
							PLACED: "It is accepted",
							CONFIRMED: "Picking starts",
							IN_PROGRESS: "It leaves the branch",
						}}
						value={draft.cancelUntil}
						onValueChange={(v) =>
							v &&
							set(
								"cancelUntil",
								v as OrderingSettings["cancelUntil"],
							)
						}
					>
						<SelectTrigger aria-label="Shoppers can cancel until">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="PLACED">
								It is accepted
							</SelectItem>
							<SelectItem value="CONFIRMED">
								Picking starts
							</SelectItem>
							<SelectItem value="IN_PROGRESS">
								It leaves the branch
							</SelectItem>
						</SelectContent>
					</Select>
				</SettingsRow>
				<SettingsRow
					label="Remind the team after"
					htmlFor="sla"
					info="A new order nobody accepted for this long is flagged and your team is notified."
				>
					<MinutesInput
						id="sla"
						value={draft.acceptSlaMinutes}
						onChange={(v) => set("acceptSlaMinutes", v ?? 5)}
					/>
				</SettingsRow>
				<SettingsRow
					label="Cancel unaccepted orders after"
					htmlFor="auto-cancel"
					description="The shopper gets an apology."
				>
					<MinutesInput
						id="auto-cancel"
						value={draft.autoCancelUnacceptedMinutes}
						onChange={(v) => set("autoCancelUnacceptedMinutes", v)}
						placeholder="Never"
					/>
				</SettingsRow>
				<SettingsRow
					label="Prices hold for"
					htmlFor="quote-ttl"
					info="After this, checkout re-checks prices and stock before the shopper confirms."
				>
					<MinutesInput
						id="quote-ttl"
						value={draft.quoteTtlMinutes}
						onChange={(v) => set("quoteTtlMinutes", v ?? 15)}
					/>
				</SettingsRow>
				<SettingsRow label="Order number prefix" htmlFor="prefix">
					<Input
						id="prefix"
						value={draft.orderNumberPrefix}
						maxLength={8}
						placeholder="H-"
						className="font-mono"
						onChange={(e) =>
							set("orderNumberPrefix", e.target.value)
						}
					/>
				</SettingsRow>
			</SettingsSection>

			<SettingsSection title="Notifications">
				<SettingsRow
					label="Email new orders to"
					htmlFor="notify"
					description="One address per line."
				>
					<Textarea
						id="notify"
						rows={3}
						value={draft.notifyEmails.join("\n")}
						placeholder="orders@yourstore.com"
						onChange={(e) =>
							set(
								"notifyEmails",
								e.target.value
									.split(/[\n,]/)
									.map((v) => v.trim())
									.filter(Boolean),
							)
						}
					/>
				</SettingsRow>
			</SettingsSection>

			<ShopperUpdatesSection
				organizationId={organizationId}
				value={draft.shopperUpdates}
				onChange={(status, on) =>
					set("shopperUpdates", {
						...draft.shopperUpdates,
						[status]: on,
					})
				}
				reported={draft.notifyReportedOrders}
				onReportedChange={(on) => set("notifyReportedOrders", on)}
			/>

			<SaveBar
				visible={dirty}
				saving={save.isPending}
				disabled={Boolean(problem)}
				message={problem ?? "You have unsaved changes"}
				onDiscard={() => setDraft(saved)}
				onSave={() => save.mutate({ organizationId, settings: draft })}
			/>
		</Page>
	);
}

function ShopperUpdatesSection({
	organizationId,
	value,
	onChange,
	reported,
	onReportedChange,
}: {
	organizationId: string;
	value: OrderingSettings["shopperUpdates"];
	onChange: (
		status: keyof OrderingSettings["shopperUpdates"],
		on: boolean,
	) => void;
	reported: boolean;
	onReportedChange: (on: boolean) => void;
}) {
	// Templates belong to WhatsApp numbers; a retailer without one simply sees no badges.
	const templates = useQuery({
		...orpc.channels.templates.list.queryOptions({
			input: { organizationId },
		}),
		retry: false,
	});
	const approved = (purpose: string) =>
		templates.data?.templates.some(
			(t) => t.purpose === purpose && t.status === "APPROVED",
		);
	return (
		<SettingsSection
			title="Messages to shoppers"
			info="Inside WhatsApp's 24-hour window the update is a free message. Outside it, it goes as an approved template, which Meta charges for."
		>
			{SHOPPER_UPDATES.map((row) => {
				const id = `update-${row.status}`;
				const hasTemplate =
					row.purpose && templates.data
						? approved(row.purpose)
						: null;
				return (
					<SettingsRow
						key={row.status}
						label={row.label}
						htmlFor={id}
						description={
							hasTemplate === false && value[row.status]
								? "No approved template: shoppers outside the 24-hour window won't get this."
								: undefined
						}
					>
						<div className="flex items-center gap-2">
							{hasTemplate === true ? (
								<Badge variant="success">
									Template approved
								</Badge>
							) : null}
							{hasTemplate === false ? (
								<Badge variant="warning">No template</Badge>
							) : null}
							<Switch
								id={id}
								checked={value[row.status]}
								onCheckedChange={(on) =>
									onChange(row.status, on)
								}
							/>
						</div>
					</SettingsRow>
				);
			})}
			<SettingsRow
				label="Orders your system reports"
				htmlFor="notify-reported"
				info="Orders your own system sent to Hephon, not placed through the agent."
			>
				<Switch
					id="notify-reported"
					checked={reported}
					onCheckedChange={onReportedChange}
				/>
			</SettingsRow>
		</SettingsSection>
	);
}

/** Why the chosen order system can't be saved yet, or null when it can. */
function backendProblem(
	kind: BackendKind,
	baseUrl: string,
	sourceId: string,
): string | null {
	if (kind === "ORDERS_API" && !/^https:\/\/\S+$/.test(baseUrl.trim())) {
		return "Enter your endpoint's https address";
	}
	if (kind === "SOURCE" && !sourceId) {
		return "Choose the platform";
	}
	return null;
}

/** The order system being edited, against the saved one. */
function useBackendDraft(backend: OrderBackend) {
	const [kind, setKind] = useState<BackendKind>(backend.kind);
	const [baseUrl, setBaseUrl] = useState(backend.baseUrl ?? "");
	const [sourceId, setSourceId] = useState(backend.sourceId ?? "");
	const [fallback, setFallback] = useState(backend.fallbackToTicket);
	const dirty =
		kind !== backend.kind ||
		(kind === "ORDERS_API" && baseUrl.trim() !== (backend.baseUrl ?? "")) ||
		(kind === "SOURCE" && sourceId !== (backend.sourceId ?? "")) ||
		fallback !== backend.fallbackToTicket;
	return {
		kind,
		setKind,
		baseUrl,
		setBaseUrl,
		sourceId,
		setSourceId,
		fallback,
		setFallback,
		dirty,
		problem: backendProblem(kind, baseUrl, sourceId),
	};
}

type BackendDraft = ReturnType<typeof useBackendDraft>;

function BackendSection({ organizationId }: { organizationId: string }) {
	const { data: backend } = useSuspenseQuery(
		orpc.orders.backend.get.queryOptions({ input: { organizationId } }),
	);
	const draft = useBackendDraft(backend);

	return (
		<SettingsSection
			title="How orders reach you"
			footer={
				<BackendActions
					organizationId={organizationId}
					savedKind={backend.kind}
					draft={draft}
				/>
			}
		>
			<BackendChoice value={draft.kind} onChange={draft.setKind} />
			{draft.kind === "ORDERS_API" ? (
				<SettingsRow label="Endpoint" htmlFor="base-url">
					<Input
						id="base-url"
						value={draft.baseUrl}
						onChange={(e) => draft.setBaseUrl(e.target.value)}
						placeholder="https://orders.yourstore.com/hephon"
						className="font-mono"
					/>
				</SettingsRow>
			) : null}
			{draft.kind === "SOURCE" ? (
				<SourcePicker
					organizationId={organizationId}
					value={draft.sourceId}
					onChange={draft.setSourceId}
				/>
			) : null}
			{draft.kind !== "TICKET" ? (
				<SettingsRow
					label="Fall back to order tickets"
					htmlFor="fallback"
					info="When your system can't take an order, it lands on the Orders screen instead of failing for the shopper."
				>
					<Switch
						id="fallback"
						checked={draft.fallback}
						onCheckedChange={draft.setFallback}
					/>
				</SettingsRow>
			) : null}
			<BackendHealth backend={backend} />
			{backend.kind === "ORDERS_API" ? (
				<DeveloperRow
					organizationId={organizationId}
					secretSet={backend.secretSet}
					openApiUrl={backend.openApiUrl}
				/>
			) : null}
		</SettingsSection>
	);
}

/** Run the checks against the saved system, and save the draft. */
function BackendActions({
	organizationId,
	savedKind,
	draft,
}: {
	organizationId: string;
	savedKind: BackendKind;
	draft: BackendDraft;
}) {
	const queryClient = useQueryClient();
	const setBackend = (next: OrderBackend) =>
		queryClient.setQueryData(
			orpc.orders.backend.get.queryKey({ input: { organizationId } }),
			next,
		);
	const update = useMutation(
		orpc.orders.backend.update.mutationOptions({
			onSuccess: (next) => {
				setBackend(next);
				toast.add({ type: "success", title: "Order system saved" });
			},
			onError: (error) =>
				actionFailed(error, "Couldn't save the order system"),
		}),
	);
	const test = useMutation(
		orpc.orders.backend.test.mutationOptions({
			onSuccess: (next) => {
				setBackend(next);
				toast.add({
					type: next.conformance?.passed ? "success" : "warning",
					title: next.conformance?.passed
						? "Every check passed"
						: "Some checks failed",
				});
			},
			onError: (error) => actionFailed(error, "Couldn't run the checks"),
		}),
	);
	const { kind, dirty, problem } = draft;
	return (
		<>
			{savedKind !== "TICKET" ? (
				<Button
					variant="outline"
					onClick={() => test.mutate({ organizationId })}
					disabled={test.isPending || dirty}
				>
					{test.isPending ? (
						<Spinner data-icon="inline-start" />
					) : null}
					Run the checks
				</Button>
			) : null}
			<DisabledReason reason={!dirty ? "Nothing changed yet" : problem}>
				<Button
					onClick={() =>
						update.mutate({
							organizationId,
							kind,
							baseUrl:
								kind === "ORDERS_API"
									? draft.baseUrl.trim()
									: null,
							sourceId: kind === "SOURCE" ? draft.sourceId : null,
							fallbackToTicket: draft.fallback,
						})
					}
					disabled={!dirty || Boolean(problem) || update.isPending}
				>
					{update.isPending ? (
						<Spinner data-icon="inline-start" />
					) : null}
					Save order system
				</Button>
			</DisabledReason>
		</>
	);
}

function BackendChoice({
	value,
	onChange,
}: {
	value: BackendKind;
	onChange: (kind: BackendKind) => void;
}) {
	const formId = useId();
	return (
		<div className="py-4">
			<RadioGroup
				value={value}
				onValueChange={(v) => onChange(v as BackendKind)}
				className="grid gap-2"
			>
				{BACKENDS.map((option) => (
					<FieldLabel
						key={option.kind}
						htmlFor={`${formId}-${option.kind}`}
					>
						<Field orientation="horizontal">
							<FieldContent>
								<FieldTitle>{option.title}</FieldTitle>
								<FieldDescription>
									{option.detail}
								</FieldDescription>
							</FieldContent>
							<RadioGroupItem
								id={`${formId}-${option.kind}`}
								value={option.kind}
							/>
						</Field>
					</FieldLabel>
				))}
			</RadioGroup>
		</div>
	);
}

/** The catalog source whose platform takes the orders. */
function SourcePicker({
	organizationId,
	value,
	onChange,
}: {
	organizationId: string;
	value: string;
	onChange: (sourceId: string) => void;
}) {
	const sources = useQuery(
		orpc.catalog.sources.list.queryOptions({ input: { organizationId } }),
	);
	const sourceItems = Object.fromEntries(
		(sources.data?.sources ?? []).map((s: { id: string; name: string }) => [
			s.id,
			s.name,
		]),
	);
	return (
		<SettingsRow label="Platform">
			<Select
				items={sourceItems}
				value={value || null}
				onValueChange={(v) => onChange(v ? String(v) : "")}
			>
				<SelectTrigger aria-label="Platform">
					<SelectValue placeholder="Choose a catalog source" />
				</SelectTrigger>
				<SelectContent>
					{Object.entries(sourceItems).map(([id, name]) => (
						<SelectItem key={id} value={id}>
							{name as string}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		</SettingsRow>
	);
}

/** What the saved order system can do, how its last checks went and its last error. */
function BackendHealth({ backend }: { backend: OrderBackend }) {
	const capabilities = Object.entries(backend.capabilities);
	return (
		<>
			{backend.kind !== "TICKET" && capabilities.length > 0 ? (
				<SettingsRow label="What it can do">
					<ul className="flex flex-col gap-1 text-sm">
						{capabilities.map(([name, on]) => (
							<li key={name} className="flex items-center gap-2">
								{on ? (
									<CheckIcon
										className="size-4 text-success"
										aria-label="Yes"
									/>
								) : (
									<CircleXIcon
										className="size-4 text-muted-foreground"
										aria-label="No"
									/>
								)}
								<span className="font-mono text-xs">
									{name}
								</span>
							</li>
						))}
					</ul>
				</SettingsRow>
			) : null}
			{backend.conformance ? (
				<ConformanceChecks results={backend.conformance.results} />
			) : null}
			{backend.lastError ? (
				<SettingsRow label="Last error">
					<span className="text-destructive text-sm">
						{backend.lastError}
					</span>
				</SettingsRow>
			) : null}
		</>
	);
}

function ConformanceChecks({
	results,
}: {
	results: NonNullable<OrderBackend["conformance"]>["results"];
}) {
	return (
		<SettingsRow
			label="Checks"
			description={`Last run passed ${results.filter((r) => r.passed).length} of ${results.length}.`}
		>
			<ul className="flex w-full flex-col gap-1 text-sm">
				{results.map((result) => (
					<li key={result.check} className="flex items-start gap-2">
						{result.passed ? (
							<CheckIcon
								className="mt-0.5 size-4 shrink-0 text-success"
								aria-label="Passed"
							/>
						) : (
							<CircleXIcon
								className="mt-0.5 size-4 shrink-0 text-destructive"
								aria-label="Failed"
							/>
						)}
						<span className="flex flex-col">
							<span>{result.check}</span>
							{result.detail ? (
								<span className="text-muted-foreground text-xs">
									{result.detail}
								</span>
							) : null}
						</span>
					</li>
				))}
			</ul>
		</SettingsRow>
	);
}

/** The signing secret for an orders endpoint, shown once when made, and the OpenAPI link. */
function DeveloperRow({
	organizationId,
	secretSet,
	openApiUrl,
}: {
	organizationId: string;
	secretSet: boolean;
	openApiUrl: string;
}) {
	const queryClient = useQueryClient();
	const [secret, setSecret] = useState<string | null>(null);
	const rotate = useMutation(
		orpc.orders.backend.rotateSecret.mutationOptions({
			onSuccess: (result) => {
				setSecret(result.secret);
				void queryClient.invalidateQueries({
					queryKey: orpc.orders.backend.get.queryKey({
						input: { organizationId },
					}),
				});
			},
			onError: (error) =>
				actionFailed(error, "Couldn't rotate the secret"),
		}),
	);
	return (
		<>
			<SettingsRow
				label="Developer"
				info="Your endpoint checks each request's signature with this secret. The old secret keeps working for a day after you rotate it."
			>
				<div className="flex flex-wrap gap-2">
					<Button
						variant="outline"
						size="sm"
						onClick={() => rotate.mutate({ organizationId })}
						disabled={rotate.isPending}
					>
						{rotate.isPending ? (
							<Spinner data-icon="inline-start" />
						) : null}
						{secretSet
							? "Rotate the signing secret"
							: "Create a signing secret"}
					</Button>
					<AnchorLinkButton
						variant="ghost"
						size="sm"
						href={openApiUrl}
						target="_blank"
						rel="noreferrer"
					>
						OpenAPI
						<ExternalLinkIcon data-icon="inline-end" />
					</AnchorLinkButton>
				</div>
			</SettingsRow>
			<Dialog
				open={secret !== null}
				onOpenChange={(open) => !open && setSecret(null)}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Your new signing secret</DialogTitle>
						<DialogDescription>
							Copy it now: it is shown once.
						</DialogDescription>
					</DialogHeader>
					{secret ? (
						<CopyField value={secret} label="Signing secret" />
					) : null}
					<DialogFooter>
						<Button onClick={() => setSecret(null)}>
							I copied it
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}

function MoneyInput({
	id,
	value,
	onChange,
	placeholder,
}: {
	id: string;
	value: number | undefined;
	onChange: (value: number | undefined) => void;
	placeholder?: string;
}) {
	return (
		<Input
			id={id}
			type="number"
			inputMode="decimal"
			min={0}
			step="0.01"
			value={value ?? ""}
			placeholder={placeholder ?? "0"}
			className="tabular"
			onChange={(e) =>
				onChange(
					e.target.value === ""
						? undefined
						: Math.max(0, Number(e.target.value)),
				)
			}
		/>
	);
}

function MinutesInput({
	id,
	value,
	onChange,
	placeholder,
}: {
	id: string;
	value: number | undefined;
	onChange: (value: number | undefined) => void;
	placeholder?: string;
}) {
	return (
		<InputGroup>
			<InputGroupInput
				id={id}
				type="number"
				inputMode="numeric"
				min={1}
				value={value ?? ""}
				placeholder={placeholder}
				className="tabular"
				onChange={(e) =>
					onChange(
						e.target.value === ""
							? undefined
							: Math.max(1, Math.round(Number(e.target.value))),
					)
				}
			/>
			<InputGroupAddon align="inline-end">min</InputGroupAddon>
		</InputGroup>
	);
}

function NumberField({
	label,
	value,
	onChange,
	min,
	max,
}: {
	label: string;
	value: number;
	onChange: (value: number | undefined) => void;
	min: number;
	max?: number;
}) {
	const id = useId();
	return (
		<Field className="gap-1">
			<FieldLabel htmlFor={id} className="text-muted-foreground text-xs">
				{label}
			</FieldLabel>
			<Input
				id={id}
				type="number"
				inputMode="numeric"
				min={min}
				max={max}
				value={value}
				className="tabular"
				onChange={(e) => {
					if (e.target.value === "") {
						onChange(undefined);
						return;
					}
					const next = Math.max(
						min,
						Math.round(Number(e.target.value)),
					);
					onChange(max ? Math.min(max, next) : next);
				}}
			/>
		</Field>
	);
}

/** The first sections of the page, with about as many rows as each has. */
const SKELETON_SECTIONS = [
	{ id: "backend", rows: 3 },
	{ id: "delivery", rows: 5 },
	{ id: "rules", rows: 4 },
] as const;

export function OrderingSetupSkeleton() {
	return (
		<Page width="narrow">
			<div className="flex flex-col gap-3">
				<Skeleton className="h-7 w-20" />
				<Skeleton className="h-8 w-48" />
			</div>
			{SKELETON_SECTIONS.map(({ id, rows }) => (
				<div
					key={id}
					className="flex flex-col gap-4 rounded-3xl p-5 ring-1 ring-foreground/6"
				>
					<Skeleton className="h-5 w-40" />
					{Array.from({ length: rows }).map((_, j) => (
						<div key={j} className="flex justify-between gap-6">
							<Skeleton className="h-4 w-32" />
							<Skeleton className="h-8 w-48" />
						</div>
					))}
				</div>
			))}
		</Page>
	);
}
